import {closeDialog, openConfirmDialog, openDialog, showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {head, isNil, isNotNil, toPairs} from 'ramda';
import {isArray, noop} from 'ramda-adjunct';

import {
  usePostTiresArchiveMutation,
  usePostTireSetDeliveryNoteMutation,
  usePostTireSetLabelMutation,
  usePostTiresRestoreMutation,
  usePostTiresTransferToOrderMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {DmsDataGrid, handleApiError, printPDF} from '@dms/shared';

import type {ActionCallback} from 'features/eag-grid';

import {groupWheelsBySet} from '../utils/groupWheelsBySet';

export const TIRE_ORDER_SET_PREDEFINED_COLUMNS = [
  'orderSetId',
  'tireManufacturer',
  'tireSeason',
  'setNumber',
] as const;

export interface EditTireSetData {
  tireOrderId: string;
  setId: string;
}

export function useTireOrderSetActions(
  orderId: string,
  onEditTireSet: (data: EditTireSetData) => void
) {
  const [postTiresArchive] = usePostTiresArchiveMutation();
  const [postTireSetDeliveryNote] = usePostTireSetDeliveryNoteMutation();
  const [postTireSetLabel] = usePostTireSetLabelMutation();
  const [postTiresTransferToOrder] = usePostTiresTransferToOrderMutation();
  const [postTiresRestore] = usePostTiresRestoreMutation();

  const actionCallback: ActionCallback<typeof TIRE_ORDER_SET_PREDEFINED_COLUMNS> = ({
    actionKey,
    rowData,
    refreshData,
    deselectAll,
  }) => {
    // Only the bulk path passes an array. Deselecting on a row action would clear a selection the
    // user is still building.
    const isBulkSelection = isArray(rowData);
    const wheelsBySet = groupWheelsBySet(rowData);

    if (isNil(wheelsBySet)) {
      showNotification.error(i18n.t('page.tiresInventory.notifications.tireSetCouldNotBeResolved'));
      return;
    }

    const setEntries = toPairs(wheelsBySet);

    // Restore is the only action allowed to span sets: setId travels in the request path, so a
    // selection covering several sets has to go out as one request per set.
    if (actionKey === 'restoreWheel') {
      return Promise.allSettled(
        setEntries.map(([restoredSetId, restoredWheels]) =>
          postTiresRestore({
            orderId,
            setId: restoredSetId,
            body: {wheels: restoredWheels},
          }).unwrap()
        )
      ).then((results) => {
        const rejection = results.find(
          (result): result is PromiseRejectedResult => result.status === 'rejected'
        );

        if (isNotNil(rejection)) {
          handleApiError(rejection.reason);
        }

        // Some sets may have been restored even when another failed, so always refresh.
        if (isBulkSelection) {
          deselectAll();
        }
        refreshData();
      });
    }

    const singleSet = setEntries.length === 1 ? head(setEntries) : null;

    if (isNil(singleSet)) {
      showNotification.error(i18n.t('page.tiresInventory.notifications.selectTiresFromOneSet'));
      return;
    }

    const [setId, wheels] = singleSet;

    const transferActionCallback: ActionCallback = ({rowId}) => {
      const targetOrderId = isArray(rowId) ? head(rowId) : rowId;

      if (isNil(targetOrderId)) {
        return;
      }

      postTiresTransferToOrder({
        targetOrderId,
        body: {
          tireSets: [{tireOrderId: orderId, tireSetId: setId}],
        },
      })
        .unwrap()
        .then(() => {
          closeDialog('transferToOrder');
          refreshData();
        })
        .catch(handleApiError);
    };

    // Returned so the grid arms its double-submit guard (`isThenable` in executeBulkAction) for
    // the branches that resolve to a promise.
    return match(actionKey)
      .with('edit', 'redirectDetail', () => {
        onEditTireSet({
          tireOrderId: orderId,
          setId,
        });
      })
      .with('printLabel', () =>
        postTireSetLabel({orderId, setId, body: {wheels}})
          .unwrap()
          .then((data) => {
            const url = data?.printout?.pdfUrl;
            if (url) {
              printPDF(url);
            } else {
              showNotification.error();
            }
          })
          .catch(handleApiError)
      )
      .with('printDeliveryNote', () =>
        postTireSetDeliveryNote({orderId, setId, body: {wheels}})
          .unwrap()
          .then((data) => {
            const url = data?.printout?.pdfUrl;
            if (url) {
              printPDF(url);
            } else {
              showNotification.error();
            }
          })
          .catch(handleApiError)
      )
      .with('archive', () =>
        openConfirmDialog({
          text: i18n.t('page.tiresInventory.labels.archiveTire'),
          onConfirm: () =>
            postTiresArchive({
              orderId,
              setId,
              body: {wheels},
            })
              .unwrap()
              .then(() => {
                // Archiving moves the rows off this tab, so the selection no longer matches.
                if (isBulkSelection) {
                  deselectAll();
                }
                refreshData();
              })
              .catch(handleApiError),
        })
      )
      .with('transferToOrder', () => {
        openDialog(
          <DmsDataGrid
            gridCode="tire-set-transfer"
            autoHeight
            actionCallback={transferActionCallback}
            data-testid={testIds.tiresInventory.tireOrderDetail('transferToOrder')}
          />,
          {
            id: 'transferToOrder',
            title: i18n.t('page.tiresInventory.labels.transferToOrder'),
            size: 'large',
          }
        );
      })
      .otherwise(noop);
  };

  return actionCallback;
}
